import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Book, Wrench, FileCheck, Settings, ClipboardList, AlertTriangle,
  Plus, Calendar, Euro, Building2, ChevronRight, Clock, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';

// Sub-components
import FicheTechniqueTab from '../components/CarnetEntretien/FicheTechniqueTab';
import EquipementsTab from '../components/CarnetEntretien/EquipementsTab';
import ContratsTab from '../components/CarnetEntretien/ContratsTab';
import TravauxTab from '../components/CarnetEntretien/TravauxTab';
import ControlesTab from '../components/CarnetEntretien/ControlesTab';
import ObservationsTab from '../components/CarnetEntretien/ObservationsTab';
import DocumentsTab from '../components/CarnetEntretien/DocumentsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CarnetEntretien() {
  const { token, user } = useAuth();
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBuildings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/buildings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
        if (data.length > 0 && !selectedBuilding) {
          setSelectedBuilding(data[0]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedBuilding]);

  const fetchStats = useCallback(async () => {
    if (!selectedBuilding) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/carnet-entretien/stats/${selectedBuilding.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [selectedBuilding, token]);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  useEffect(() => {
    if (selectedBuilding) {
      fetchStats();
    }
  }, [selectedBuilding, fetchStats]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Book },
    { id: 'fiche-technique', label: 'Fiche technique', icon: Building2 },
    { id: 'equipements', label: 'Équipements', icon: Settings },
    { id: 'contrats', label: 'Contrats', icon: FileCheck },
    { id: 'travaux', label: 'Travaux', icon: Wrench },
    { id: 'controles', label: 'Contrôles obligatoires', icon: ClipboardList },
    { id: 'observations', label: 'Observations', icon: Eye },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Book className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Carnet d'entretien</h1>
            <p className="text-gray-500">Suivi technique et réglementaire de l'immeuble</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* PDF Export Button */}
          <button
            onClick={async () => {
              if (!selectedBuilding) return;
              try {
                const response = await fetch(`${API_URL}/api/carnet-entretien/export-pdf/${selectedBuilding.id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Carnet_Entretien_${selectedBuilding.nom || 'immeuble'}_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  a.remove();
                  toast.success('PDF téléchargé');
                } else {
                  toast.error('Erreur lors du téléchargement');
                }
              } catch (error) {
                toast.error('Erreur de connexion');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-700"
            data-testid="export-pdf-btn"
          >
            <FileText className="h-5 w-5" />
            Exporter PDF
          </button>
          
          {/* Building Selector */}
          <select
            value={selectedBuilding?.id || ''}
            onChange={(e) => {
              const building = buildings.find(b => b.id === e.target.value);
              setSelectedBuilding(building);
            }}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white min-w-[250px]"
            data-testid="building-selector"
          >
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.nom || b.adresse}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs - Style unifié avec coins arrondis en haut */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex flex-wrap gap-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
                  isActive
                    ? 'bg-[#00D3A8] text-white'
                    : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dashboard */}
      {activeTab === 'dashboard' && stats && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Équipements</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.nb_equipements}</p>
                </div>
                <Settings className="h-10 w-10 text-blue-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Contrats actifs</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.nb_contrats_actifs}</p>
                </div>
                <FileCheck className="h-10 w-10 text-green-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Travaux {new Date().getFullYear()}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.nb_travaux_annee}</p>
                  <p className="text-sm text-gray-400">{formatMoney(stats.total_travaux_annee)}</p>
                </div>
                <Wrench className="h-10 w-10 text-orange-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Contrôles à venir</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stats.controles_a_venir}</p>
                  {stats.controles_en_retard > 0 && (
                    <p className="text-sm text-red-500">{stats.controles_en_retard} en retard</p>
                  )}
                </div>
                <ClipboardList className="h-10 w-10 text-purple-200" />
              </div>
            </div>
          </div>

          {/* Alerts */}
          {stats.controles_en_retard > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                <div>
                  <h3 className="font-semibold text-red-800">Attention - Contrôles en retard</h3>
                  <p className="text-sm text-red-600">
                    {stats.controles_en_retard} contrôle(s) obligatoire(s) dépassé(s). 
                    Consultez l'onglet "Contrôles obligatoires" pour régulariser.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('travaux')}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-[#00D3A8] hover:shadow-md transition-all"
            >
              <div className="p-3 bg-orange-100 rounded-lg">
                <Plus className="h-5 w-5 text-orange-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Ajouter des travaux</p>
                <p className="text-sm text-gray-500">Enregistrer une intervention</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </button>
            
            <button
              onClick={() => setActiveTab('controles')}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-[#00D3A8] hover:shadow-md transition-all"
            >
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Planifier un contrôle</p>
                <p className="text-sm text-gray-500">Échéances réglementaires</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </button>
            
            <button
              onClick={() => setActiveTab('contrats')}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-[#00D3A8] hover:shadow-md transition-all"
            >
              <div className="p-3 bg-green-100 rounded-lg">
                <FileCheck className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Gérer les contrats</p>
                <p className="text-sm text-gray-500">Maintenance et entretien</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
            </button>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Book className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Carnet d'entretien obligatoire</h4>
                <p className="text-sm text-blue-600 mt-1">
                  Le carnet d'entretien est obligatoire pour toute copropriété (loi SRU du 13/12/2000). 
                  Il doit être tenu à jour et mis à disposition des coproprietaires.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other Tabs */}
      {activeTab === 'fiche-technique' && selectedBuilding && (
        <FicheTechniqueTab buildingId={selectedBuilding.id} token={token} />
      )}
      
      {activeTab === 'equipements' && selectedBuilding && (
        <EquipementsTab buildingId={selectedBuilding.id} token={token} />
      )}
      
      {activeTab === 'contrats' && selectedBuilding && (
        <ContratsTab buildingId={selectedBuilding.id} token={token} />
      )}
      
      {activeTab === 'travaux' && selectedBuilding && (
        <TravauxTab buildingId={selectedBuilding.id} token={token} onUpdate={fetchStats} />
      )}
      
      {activeTab === 'controles' && selectedBuilding && (
        <ControlesTab buildingId={selectedBuilding.id} token={token} onUpdate={fetchStats} />
      )}
      
      {activeTab === 'observations' && selectedBuilding && (
        <ObservationsTab buildingId={selectedBuilding.id} token={token} />
      )}
      
      {activeTab === 'documents' && selectedBuilding && (
        <DocumentsTab buildingId={selectedBuilding.id} token={token} />
      )}
    </div>
  );
}
